import React, { createContext, useContext, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

const ConfirmContext = createContext(null);

/**
 * ConfirmProvider Component
 * Provides confirmation dialog functionality globally
 */
export const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  const confirm = (options) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title: options.title || 'Confirm Action',
        message: options.message || 'Are you sure?',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
        variant: options.variant || 'warning',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
      });
    });
  };

  const close = () => {
    if (confirmState.onCancel) {
      confirmState.onCancel();
    }
    setConfirmState({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      onCancel: null,
      variant: 'warning',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
    });
  };

  const handleConfirm = () => {
    if (confirmState.onConfirm) {
      confirmState.onConfirm();
    }
    close();
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={close}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * useConfirmContext Hook
 * Access confirm function from context
 */
export const useConfirmContext = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirmContext must be used within ConfirmProvider');
  }
  return context;
};

export default ConfirmProvider;
